import {
  BarChartBoxAiFill,
  DashboardFill,
  InactiveObservable,
  Information2Fill,
  LoginCircleFill,
  PulseAiLine,
  SearchEyeFill,
  Database2Fill,
  QuestionFill,
} from "@ui/IconV2/react";
import { colors } from "@ui/Theme";
import {
  AGGREGATE_NO_SYSTEM,
  AGGREGATE_UNEXPLAINED_LOSSES,
  useAvailableResources,
} from "../../../../../hook/useAvailableResources";
import type { Annotation } from "./Annotation/hook/useAnnotations";

export type Role =
  | "MODEL_INPUT"
  | "OBSERVABLE"
  | "INACTIVE_OBSERVABLE"
  | "PERFORMANCE_INDICATOR"
  | "FAULT"
  | "SYSTEM"
  | "QUANTITY"
  | "UNEXPLAINED_LOSSES"
  | "OTHER_QUANTITY";

export function getRoleColor(role: Role): string {
  switch (role) {
    case "FAULT":
      return "#9467bd";
    case "SYSTEM":
      return "#8c564b";
    case "INACTIVE_OBSERVABLE":
      return colors.titanium500;
    case "UNEXPLAINED_LOSSES":
      return colors.titanium800;
    default:
      return colors.chart_categorical_blue;
  }
}

export function getRoleIcon(role: Role) {
  switch (role) {
    case "MODEL_INPUT":
      return LoginCircleFill;
    case "OBSERVABLE":
      return SearchEyeFill;
    case "INACTIVE_OBSERVABLE":
      return InactiveObservable;
    case "PERFORMANCE_INDICATOR":
      return BarChartBoxAiFill;
    case "FAULT":
      return PulseAiLine;
    case "SYSTEM":
      return DashboardFill;
    case "QUANTITY":
      return Database2Fill;
    case "UNEXPLAINED_LOSSES":
      return QuestionFill;
    default:
      return Information2Fill;
  }
}

export function resolveRole(
  annotation: Pick<Annotation, "type" | "name" | "encodings">,
  availableResources: ReturnType<typeof useAvailableResources>,
  isRoleDisplayed: boolean,
): Role {
  const typeIdentifier = annotation.encodings?.[0]?.identifier;

  const isPerformanceIndicator = availableResources.performanceIndicators.some(
    p => p.name === typeIdentifier,
  );
  if (isPerformanceIndicator) return "PERFORMANCE_INDICATOR";

  if (annotation.type === "FAULT") return "FAULT";

  if (
    annotation.type === "SYSTEM" ||
    (annotation.type === "AGGREGATE" && typeIdentifier === AGGREGATE_NO_SYSTEM)
  ) {
    return "SYSTEM";
  }

  if (annotation.type === "AGGREGATE" && typeIdentifier === AGGREGATE_UNEXPLAINED_LOSSES)
    return "UNEXPLAINED_LOSSES";

  const isSimulationInput = availableResources.quantity.identifiers.some(
    q => q.name === typeIdentifier && q.usedForSimulationInputs,
  );
  const isObservable = availableResources.observables.some(
    q => q.name === typeIdentifier && q.enabled,
  );
  const isDisabledObservable = availableResources.observables.some(
    q => q.name === typeIdentifier && !q.enabled,
  );

  if (isSimulationInput) return "MODEL_INPUT";
  if (isObservable) return "OBSERVABLE";
  if (isDisabledObservable) return "INACTIVE_OBSERVABLE";

  if (annotation.type === "QUANTITY") return isRoleDisplayed ? "OTHER_QUANTITY" : "QUANTITY";

  return "OTHER_QUANTITY";
}
